import React, { ChangeEvent, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Field } from "model/robot";
import { RobotUserSchemaButtonWrapper } from "./RobotUserSchemaButtonWrapper";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface RobotFormFieldProps {
  fieldKey: string;
  def: Field;
  value: any;
  isRequired: boolean;
  handleChange: (key: string, value: any) => void;
  handleFileChange: (key: string, e: ChangeEvent<HTMLInputElement>) => void;
  onUserDataTrigger?: (
    fieldName: string,
    anchorEl: HTMLElement,
  ) => void;
  userdataschema?: any[];
}

export const RobotFormField: React.FC<RobotFormFieldProps> = ({
  fieldKey: key,
  def,
  value,
  isRequired,
  handleChange,
  handleFileChange,
  onUserDataTrigger,
  userdataschema,
}) => {
  const translate = useTranslations()
  const [showPassword, setShowPassword] = useState(false);
  const format = def.format;
  const isFile =
    (def.format === "binary" && def.type === "string") ||
    (def.contentEncoding === "base64" && def.contentMediaType);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    handleChange(key, newValue);
  };

  let fieldContent;
  const isUserSchema = typeof value === 'string' && value.startsWith('{{');
  const isTextarea = def.type === "string" && format === "textarea";

  if (def.type === "string" && format === "date") {
    fieldContent = (
      <TextField
        key={key}
        label={key}
        type={isUserSchema ? 'text' : 'date'}
        fullWidth
        margin="dense"
        InputLabelProps={{ shrink: true }}
        required={isRequired}
        value={value || ""}
        onChange={handleTextChange}
      />
    );
  } else if (isFile) {
    const isFromUserData = typeof value === 'string' && value.startsWith('{{');
    fieldContent = (
      <Box key={key} sx={{ my: 1 }}>
        <Button variant="contained" component="label">
          {getTranslation(translate, "generic.load")}{key}
          <input
            type="file"
            hidden
            onChange={(e) => handleFileChange(key, e)}
          />
        </Button>
        {value && (
          <Typography variant="body2">
            {isFromUserData ? `Caricato da schema ${value}` : getTranslation(translate, "generic.loading_success")}
          </Typography>
        )}
      </Box>
    );
  } else if (def.type === 'password') {

    fieldContent = (
      <TextField
        key={key}
        label={key}
        type={showPassword ? 'text' : 'password'}
        fullWidth
        margin="dense"
        required={isRequired}
        value={value || ""}
        onChange={handleTextChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
    );
  } else if (def.type === "string" || def.type === "dropdown") {
    if (def.enum && Array.isArray(def.enum)) {
      fieldContent = (
        <TextField
          key={key}
          label={key}
          select
          fullWidth
          margin="dense"
          required={isRequired}
          value={value || ""}
          onChange={(e) => handleChange(key, e.target.value)}
          InputLabelProps={{ shrink: isRequired || !!value }}
          SelectProps={{
            native: true,
          }}
        >
          {!isRequired && <option value=""></option>}
          {def.enum.map((opt: string, i: number) => (
            <option key={opt} value={opt}>
              {(def as any)["x-enum-varnames"]?.[i] || opt}
            </option>
          ))}
        </TextField>
      );
    }
    else {
      fieldContent = (
        <TextField
          key={key}
          label={key}
          type="text"
          fullWidth
          multiline={isTextarea}
          rows={isTextarea ? 4 : undefined}
          margin="dense"
          required={isRequired}
          value={value || ""}
          onChange={handleTextChange}
        />
      );
    }
  } else if (def.type === "number") {
    fieldContent = (
      <TextField
        key={key}
        label={key}
        type={isUserSchema ? 'text' : 'number'}
        fullWidth
        margin="dense"
        required={isRequired}
        value={value !== undefined && value !== null ? value : ""}
        onChange={(e) => {
          const v = e.target.value;
          if (v.includes('{{')) {
            handleChange(key, v);
          } else {
            handleChange(key, v === "" ? "" : Number(v));
          }
        }}
      />
    );
  } else if (def.type === "boolean") {
    fieldContent = (
      <Box key={key} sx={{ my: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isUserSchema ? false : !!value}
              onChange={(e) => handleChange(key, e.target.checked)}
              disabled={isUserSchema}
            />
          }
          label={key}
        />
        {isUserSchema && (
          <Box sx={{ ml: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Caricato da userschema {value}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleChange(key, false)}
            >
              Riabilita
            </Button>
          </Box>
        )}
      </Box>
    );
  } else {
    fieldContent = (
      <TextField
        key={key}
        label={key}
        fullWidth
        margin="dense"
        required={isRequired}
        value={value || ""}
        onChange={handleTextChange}
      />
    );
  }

  //e non lo facciamo vedere nemmeno sul dropdown
  if (def.enum && Array.isArray(def.enum)) {
    return fieldContent;
  }
  //a tutti gli altri gli mettiamo il bottone
  return (
    <RobotUserSchemaButtonWrapper
      fieldKey={key}
      onUserDataTrigger={onUserDataTrigger}
      userdataschema={userdataschema}
    >
      {fieldContent}
    </RobotUserSchemaButtonWrapper>
  );
};